import React from 'react';
import { renderName, makeNative } from "./store";
import Lowlight from 'react-lowlight';

import c from 'highlight.js/lib/languages/cpp';
import lua from 'highlight.js/lib/languages/lua';
import cs from 'highlight.js/lib/languages/cs';
import js from 'highlight.js/lib/languages/javascript';
import ts from 'highlight.js/lib/languages/typescript';

import remark from 'remark';
import reactRenderer from 'remark-react';

import RemarkLowlight from 'remark-react-lowlight';

import 'highlight.js/styles/github-gist.css';

import githubSchema from 'hast-util-sanitize/lib/github.json';
import { navigateTo } from './navutils';


export const formatParams = (native) => native.params.map(param => `${param.type} ${param.name}`).join(', ');

export const CLanguage = {
  name: 'c',
  lowlightName: 'cpp',
  renderName(native) {
    return native.name || native.hash;
  },
  formatNativeCode(native) {
    return `// ${makeNative(renderName(native))}
${native.results} ${renderName(native)}(${formatParams(native)});`;
  },
  formatRefParams(native) {
    return formatParams(native);
  }
};

export const LuaLanguage = {
  name: 'lua',
  lowlightName: 'lua',
  renderName(native) {
    return makeNative(renderName(native));
  },
  formatNativeCode(native) {
    return `-- ${renderName(native)}
${this.formatResults(native)}${this.renderName(native)}(${this.formatParams(native)})
`;
  },

  mapType(type) {
    switch (type) {
      case 'BOOL':
        return 'boolean';
      case 'int':
        return 'integer';
      case 'float':
        return 'number';
      case 'char*':
        return 'string';
      case 'Vector3':
        return 'vector3';
    }

    return type;
  },

  formatResults(native) {
    const results = [];

    if (native.results !== 'void') {
      results.push([this.mapType(native.results), 'retval']);
    }

    results.push(...native.params
      .filter(param => /\*$/.test(param.type) && param.type !== 'char*')
      .map(param => [this.mapType(param.type.replace('*', '')), param.name])
    );

    if (results.length === 0) {
      return '';
    }

    return 'local ' +
      results.map(([type, name]) => `${name} --[[ ${type} ]]`).join(', ') +
      ' =\n\t';
  },

  formatParams(native) {
    const tabs = (this.formatResults(native).length === 0) ? '' : '\t';

    const pl = native.params
      .filter(param => !/\*$/.test(param.type) || param.type === 'char*')
      .map(param => `${param.name} --[[ ${this.mapType(param.type)} ]]`)
      .join(`, \n${tabs}\t`);

    if (pl.length === 0) {
      return '';
    }

    return `\n${tabs}\t${pl}\n${tabs}`;
  },

  formatRefParams(native) {
    return this.formatParams(native).replace(/\n\t/g, '').trim();
  }
};

export const CSLanguage = {
  name: 'c#',
  lowlightName: 'cs',

  renderName(native) {
    return makeNative(renderName(native));
  },

  formatNativeCode(native) {
    return `// ${renderName(native)}
${this.mapType(native.results)} ${this.renderName(native)}(${this.formatParams(native)});`;
  },

  mapType(type) {
    switch (type) {
      case 'char*':
        return 'string';
      case 'BOOL':
        return 'bool';
      case 'Hash':
        return 'uint';
      case 'Entity':
      case 'Ped':
      case 'Object':
      case 'Vehicle':
        return `int /* ${type} */`;
    }

    if (/\*$/.test(type)) {
      type = `ref ${this.mapType(type.replace('*', ''))}`;
    }

    return type;
  },

  formatParams(native) {
    return native.params.map(param => `${this.mapType(param.type)} ${param.name}`).join(', ');
  },

  formatRefParams(native) {
    return this.formatParams(native);
  }
};

export const JSLanguage = {
  name: 'js',
  lowlightName: 'typescript', // since we do type annotations
  renderName(native) {
    return makeNative(renderName(native));
  },

  formatNativeCode(native) {
    return `// ${renderName(native)}
${this.formatResults(native)}${this.renderName(native)}(${this.formatParams(native)});
`;
  },

  mapType(type) {
    switch (type) {
      case 'BOOL':
        return 'boolean';
      case 'int':
        return 'number';
      case 'float':
        return 'number';
      case 'char*':
        return 'string';
      case 'Vector3':
        return '[ number, number, number ]';
    }

    return type;
  },

  formatResults(native) {
    const results = [];

    if (native.results !== 'void') {
      results.push([this.mapType(native.results), 'retval']);
    }

    results.push(...native.params
      .filter(param => /\*$/.test(param.type) && param.type !== 'char*')
      .map(param => [this.mapType(param.type.replace('*', '')), param.name])
    );

    const names = results.map(([t, n]) => n).join(', ');
    const types = results.map(([t, n]) => t).join(', ');

    if (results.length === 0) {
      return '';
    } else if (results.length === 1) {
      return `const ${names}: ${types} = \n\t`;
    } else {
      return `const [${names}]: [${types}] = \n\t`;
    }
  },

  formatParams(native) {
    const tabs = (this.formatResults(native).length === 0) ? '' : '\t';

    const pl = native.params
      .filter(param => !/\*$/.test(param.type) || param.type === 'char*')
      .map(param => `${param.name}: ${this.mapType(param.type)}`)
      .join(`, \n${tabs}\t`);

    if (pl.length === 0) {
      return '';
    }

    return `\n${tabs}\t${pl}\n${tabs}`;
  },

  formatRefParams(native) {
    return this.formatParams(native).replace(/\n\t/g, '').trim();
  }
};

Lowlight.registerLanguage('js', js);
Lowlight.registerLanguage('cpp', c);
Lowlight.registerLanguage('c', c);
Lowlight.registerLanguage('lua', lua);
Lowlight.registerLanguage('cs', cs);
Lowlight.registerLanguage('csharp', cs);
Lowlight.registerLanguage('ts', ts);
Lowlight.registerLanguage('typescript', ts);

const schema = Object.assign({}, githubSchema, {
  attributes: Object.assign({}, githubSchema.attributes, {
    code: [
      ...(githubSchema.attributes.code || []),
      'className'
    ]
  })
});

const NoParagraph = ({ children }) => {
  return (<p className="inlined">{children}</p>);
};

const NoDiv = ({ children }) => {
  return (<div className="inlined">{children}</div>);
};

const AParagraph = ({ children }) => {
  return (<p>{children}</p>);
};

const ADiv = ({ children }) => {
  return (<div>{children}</div>);
};

const A = ({ children, href }) => {
  if (href[0] === '#') {
    const actualHref = href.replace('#', '?');

    const handleClick = React.useCallback((event) => {
      event.preventDefault();


      navigateTo(actualHref);

      return false;
    }, [href]);

    return (
      <a href={actualHref} onClick={handleClick}>{children}</a>
    );
  }
  return (
    <a href={href} target="_blank">{children}</a>
  );
};

export const mdify = (text, noP) => {
  return remark().use(reactRenderer, {
    sanitize: schema,
    remarkReactComponents: {
      code: RemarkLowlight({
        js,
        lua,
        c,
        cs
      }),
      a: A,
      p: (noP) ? NoParagraph : AParagraph,
      div: (noP) ? NoDiv : ADiv
    }
  }).processSync(text).contents;
};
